import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import {
    Box,
    Button,
    Chip,
    Drawer,
    IconButton,
    InputAdornment,
    Link,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LocalPrintshopOutlinedIcon from "@mui/icons-material/LocalPrintshopOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import CallMergeOutlinedIcon from "@mui/icons-material/CallMergeOutlined";
import CallSplitOutlinedIcon from "@mui/icons-material/CallSplitOutlined";
import PostData from "../../services/PostData.js";
import useApiData from "../../services/useApiData.js";
import { currencyFormatter } from "../Helpers/CurrencyFormat.js";
import CancelOrderDialog from "./CancelOrderDialog.jsx";
import VoidLabelDialog from "./VoidLabelDialog.jsx";
import CombineShipmentsDialog from "./CombineShipmentsDialog.jsx";
import PrintOrder from "./PrintOrder.jsx";
import PullOrdersSplitButton from "../Helpers/PullOrdersSplitButton.jsx";
import GridFilterBar, { useGridFilter } from "../Helpers/GridFilterBar.jsx";
import GridLayoutControls from "../Helpers/GridLayoutControls.jsx";
import useGridLayout from "../Helpers/useGridLayout.js";
import { GRID_LAYOUT_KEYS } from "../Helpers/gridLayoutKeys.js";
import { SHIPMENT_FILTER_FIELDS } from "../Helpers/gridFilterFields.js";
import FloatingAssetPreview from "../Helpers/FloatingAssetPreview.jsx";
import { assetFetchKey } from "../Helpers/assetFetch.js";
import SnackbarEx from "../Helpers/SnackbarEx.jsx";
import GridRowContextMenu, {
    buildShipmentShareUrl,
    copyShareUrlAction,
    useGridRowContextMenu,
    useSyncedUrlSearchQuery,
} from "../Helpers/GridRowContextMenu.jsx";
import {
    catalogDataGridSx,
    catalogQuietButtonSx,
    catalogSearchFieldSx,
    appDataGridDefaultProps,
    appDataGridSlotProps,
    emptySelectionModel,
    selectionIds,
} from "../Products/catalogGridStyles.js";
import "../Products/catalog.css";

const DEFAULT_SHIPMENT_SORT = [{ field: "dateCreated", sort: "desc" }];
const CLIENT_ONLY_SHIPMENT_FILTER_FIELDS = new Set(["pickReady"]);
const NON_SORTABLE_SHIPMENT_FIELDS = new Set([
    "pickReady",
    "catalogItems",
    "status",
    "labelAsset",
    "cancel",
    "voidLabel",
    "trackingUrl",
    "combineGroupId",
]);

function canVoidBreadBsktLabel(row) {
    const via = String(row?.shippedVia || "").trim();
    if (via !== "Bread Bskt") return false;
    const tokens = String(row?.trackingNumber || "")
        .split(/[,;\s]+/)
        .map((part) => part.trim())
        .filter(Boolean);
    return tokens.some((token) => /^1Z/i.test(token));
}

function normalizeCombineToken(value) {
    return String(value || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");
}

function normalizeCombineZip(value) {
    return String(value || "")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");
}

function buyerCombineKeyFromRow(row) {
    const channel = normalizeCombineToken(row?.orderChannel || row?.sellingChannel);
    if (!channel) return "";
    const username = normalizeCombineToken(row?.raw?.buyerUsername || row?.buyerUsername);
    if (channel === "ebay" && username) return `ebay:u:${username}`;
    const email = normalizeCombineToken(row?.raw?.buyerEmail || row?.buyerEmail);
    if (email) return `${channel}:e:${email}`;
    const name = normalizeCombineToken(row?.buyerName);
    const address = normalizeCombineToken(row?.address1 || row?.raw?.address1);
    const zip = normalizeCombineZip(row?.shipZip || row?.raw?.shipToZip);
    if (!name || !zip) return "";
    return `${channel}:a:${name}|${address}|${zip}`;
}

function shipToCombineKeyFromRow(row) {
    return [
        normalizeCombineToken(row?.address1 || row?.raw?.address1),
        normalizeCombineToken(row?.raw?.address2),
        normalizeCombineToken(row?.shipCity || row?.raw?.shipToCity),
        normalizeCombineToken(row?.shipState || row?.raw?.shipToState),
        normalizeCombineZip(row?.shipZip || row?.raw?.shipToZip),
        normalizeCombineToken(row?.raw?.shipToCountry || "us") || "us",
    ].join("|");
}

/** Client-side mirror of combine eligibility (Needs Label, same buyer/channel/ship-to). */
function canCombineShipmentRows(selectedRows) {
    if (!Array.isArray(selectedRows) || selectedRows.length < 2) {
        return { ok: false, message: "Select at least two Needs Label shipments." };
    }
    if (selectedRows.some((row) => row.status === "Shipped")) {
        return {
            ok: false,
            message: "Void existing labels before combining (all must be Needs Label).",
        };
    }
    const buyers = new Set(selectedRows.map(buyerCombineKeyFromRow).filter(Boolean));
    if (buyers.size !== 1) {
        return { ok: false, message: "Shipments must belong to the same buyer." };
    }
    const channels = new Set(
        selectedRows
            .map((row) => normalizeCombineToken(row.orderChannel))
            .filter(Boolean)
    );
    if (channels.size !== 1) {
        return { ok: false, message: "Shipments must share the same selling channel." };
    }
    const shipTos = new Set(selectedRows.map(shipToCombineKeyFromRow));
    if (shipTos.size !== 1) {
        return { ok: false, message: "Shipments must share the same ship-to address." };
    }
    return { ok: true };
}

function canUncombineShipmentRows(selectedRows) {
    if (!Array.isArray(selectedRows) || !selectedRows.length) return false;
    if (selectedRows.some((row) => row.status === "Shipped")) return false;
    const groups = [
        ...new Set(
            selectedRows
                .map((row) => String(row.combineGroupId || "").trim())
                .filter(Boolean)
        ),
    ];
    return groups.length === 1;
}

function dayInputValue(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

function defaultPullRange() {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 3);
    return { startDate: dayInputValue(start), endDate: dayInputValue(end) };
}

function pullRangeError(startDate, endDate) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate) || !/^\d{4}-\d{2}-\d{2}$/.test(endDate)) {
        return "Choose a start and end date.";
    }
    if (startDate > endDate) return "Start date must be on or before the end date.";
    const start = new Date(`${startDate}T00:00:00`);
    const end = new Date(`${endDate}T00:00:00`);
    const days = Math.round((end.getTime() - start.getTime()) / 86400000);
    if (days > 62) return "Date range cannot be longer than 62 days.";
    return "";
}

function catalogTabForStatus(status) {
    switch (status) {
        case "Awaiting Pickup":
            return 0;
        case "Inspection":
            return 2;
        case "Putaway":
            return 3;
        case "Listed":
            return 4;
        case "Missing":
            return 5;
        default:
            return 6;
    }
}

function formatDate(value) {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString();
}

function channelChipSx(channel) {
    const key = String(channel || "").toLowerCase();
    const base = {
        height: 24,
        fontWeight: 600,
        fontSize: 12,
        borderRadius: "8px",
    };
    if (key === "ebay") {
        return { ...base, backgroundColor: "#fef3c7", color: "#854d0e", border: "1px solid #fcd34d" };
    }
    if (key === "walmart") {
        return { ...base, backgroundColor: "#dbeafe", color: "#1e40af", border: "1px solid #93c5fd" };
    }
    return { ...base, backgroundColor: "#f1f5f9", color: "#475569" };
}

function statusChipSx(status) {
    if (status === "Needs Label") {
        return {
            height: 24,
            fontWeight: 700,
            fontSize: 12,
            borderRadius: "8px",
            backgroundColor: "#fff7ed",
            color: "#c2410c",
            border: "1px solid #fdba74",
            cursor: "pointer",
        };
    }
    return {
        height: 24,
        fontWeight: 700,
        fontSize: 12,
        borderRadius: "8px",
        backgroundColor: "#ecfdf5",
        color: "#047857",
        border: "1px solid #6ee7b7",
    };
}

function linkedCatalogItems(doc) {
    const many = Array.isArray(doc?.catalogItems) ? doc.catalogItems.filter((item) => item && item._id) : [];
    if (many.length) return many;
    if (doc?.catalogItem?._id) return [doc.catalogItem];
    return [];
}

function isCatalogItemPicked(item) {
    return Boolean(item?.pickedAt) || String(item?.status || "") === "Picked";
}

/** All assigned units picked and enough units for the order quantity. */
function pickReadyState(doc, linked) {
    const qty = Math.max(1, Math.floor(Number(doc?.quantity) || 1));
    const assigned = linked.length;
    const picked = linked.filter(isCatalogItemPicked).length;
    const ready = assigned >= qty && picked >= qty && assigned > 0;
    return {
        ready,
        pickReady: ready ? "Ready" : "Not ready",
        pickedCount: picked,
        neededCount: Math.max(qty, assigned),
    };
}

function pickReadyChipSx(ready) {
    if (ready) {
        return {
            height: 24,
            fontWeight: 700,
            fontSize: 12,
            borderRadius: "8px",
            backgroundColor: "#ecfdf5",
            color: "#047857",
            border: "1px solid #6ee7b7",
        };
    }
    return {
        height: 24,
        fontWeight: 600,
        fontSize: 12,
        borderRadius: "8px",
        backgroundColor: "#f8fafc",
        color: "#64748b",
        border: "1px solid #cbd5e1",
    };
}

function hasShippingLabelAsset(label) {
    return Boolean(assetFetchKey(label));
}

/** Already fulfilled — hide from Needs label even without a GCS label asset. */
function shipmentLooksShipped(doc) {
    return (
        Boolean(doc?.shippedAt) ||
        Boolean(String(doc?.trackingNumber || "").trim()) ||
        hasShippingLabelAsset(doc?.shippingLabel?.[0])
    );
}

function toPrintRecord(doc) {
    if (doc?.fields) return doc;
    const label = doc.shippingLabel?.[0];
    const labelUrl = assetFetchKey(label);
    // Only pass a real Airtable record id — never fall back to Mongo ObjectId
    // (Airtable update rejects non-`rec…` ids with INVALID_RECORDS).
    const airtableId =
        typeof doc.airtableId === "string" && doc.airtableId.startsWith("rec")
            ? doc.airtableId
            : "";
    return {
        id: airtableId,
        mongoId: String(doc._id),
        shipmentType: doc.shipmentType || "",
        combineGroupId: doc.combineGroupId || "",
        catalogItems: linkedCatalogItems(doc),
        fields: {
            "Order Number": doc.orderNumber || "",
            "Item ID": doc.itemId || "",
            "Buyer Name": doc.buyerName || "",
            "Address 1": doc.address1 || "",
            "Address 2": doc.address2 || "",
            "Ship to city": doc.shipToCity || "",
            "Ship to province/region/state": doc.shipToState || "",
            "Ship to zip": doc.shipToZip || "",
            "Ship to country": doc.shipToCountry || "",
            "Selling channel": doc.sellingChannel || "",
            Quantity: doc.quantity,
            "eBay Line Item Id": doc.ebayLineItemId || "",
            "Tracking Number": doc.trackingNumber || "",
            "Shipment Type": doc.shipmentType || "",
            "Shipping Label": labelUrl
                ? [{ url: labelUrl, filename: label?.filename || "" }]
                : [],
        },
    };
}

function mapShipmentRow(order) {
    const labelFile = order.shippingLabel?.[0];
    const linked = linkedCatalogItems(order);
    const pick = pickReadyState(order, linked);
    const shipped = shipmentLooksShipped(order);
    return {
        id: String(order._id),
        orderMongoId: order.order ? String(order.order) : "",
        trackingNumber: order.trackingNumber || "",
        trackingUrl: order.trackingUrl || "",
        customerOrderNumber: order.orderNumber || "",
        itemId: order.itemId || "",
        catalogItems: linked,
        orderChannel: order.sellingChannel || "",
        pickReady: pick.pickReady,
        pickReadyYes: pick.ready,
        pickedCount: pick.pickedCount,
        neededCount: pick.neededCount,
        status: shipped ? "Shipped" : "Needs Label",
        shippedVia: order.shippedVia || "",
        combineGroupId: order.combineGroupId || "",
        buyerName: order.buyerName || "",
        buyerPendingOrderCount: Math.max(
            1,
            Math.floor(Number(order.buyerPendingOrderCount) || 1)
        ),
        shippingService: order.shippingService || "",
        labelCost: order.labelCost,
        labelAsset: hasShippingLabelAsset(labelFile) ? labelFile : null,
        quantity: order.quantity ?? "",
        weight: order.weightLabel ?? "",
        length: order.lengthLabel ?? "",
        width: order.widthLabel ?? "",
        height: order.heightLabel ?? "",
        shipCity: order.shipToCity || "",
        shipState: order.shipToState || "",
        shipZip: order.shipToZip || "",
        address1: order.address1 || "",
        dateCreated: formatDate(order.airtableCreatedAt || order.createdAt),
        raw: order,
    };
}

export default function ShipmentGrid({ view = "needs-label" }) {
    const filter =
        view === "all" || view === "ready-to-ship" || view === "needs-label"
            ? view
            : "needs-label";
    const { searchInput, setSearchInput, searchQ, searchParams } =
        useSyncedUrlSearchQuery();
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
    const [rowCount, setRowCount] = useState(0);
    const [orders, setOrders] = useState([]);
    const [pulling, setPulling] = useState(false);
    const [pullError, setPullError] = useState("");
    const [pullMessage, setPullMessage] = useState("");
    const initialRange = useMemo(() => defaultPullRange(), []);
    const [startDate, setStartDate] = useState(initialRange.startDate);
    const [endDate, setEndDate] = useState(initialRange.endDate);
    const rangeError = pullRangeError(startDate, endDate);
    const [printOpen, setPrintOpen] = useState(false);
    const [selectedRecords, setSelectedRecords] = useState([]);
    const [selectionModel, setSelectionModel] = useState(() => emptySelectionModel());
    const [cancelOpen, setCancelOpen] = useState(false);
    const [cancelOrder, setCancelOrder] = useState(null);
    const [voidOpen, setVoidOpen] = useState(false);
    const [voidShipment, setVoidShipment] = useState(null);
    const [combineOpen, setCombineOpen] = useState(false);
    const [combineRows, setCombineRows] = useState([]);
    const [uncombineLoad, setUncombineLoad] = useState(false);
    const [snack, setSnack] = useState({ open: false, color: "", message: "" });
    const {
        menu: rowMenu,
        close: closeRowMenu,
        rowContextMenuSlotProps,
    } = useGridRowContextMenu();
    const [labelPreview, setLabelPreview] = useState({
        open: false,
        asset: null,
        title: "",
    });
    const {
        apply: applyFilter,
        activeConditions,
        conjunction: gridConjunction,
        barProps: filterBar,
    } = useGridFilter(SHIPMENT_FILTER_FIELDS);

    useEffect(() => {
        setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    }, [searchQ]);

    const shareTab = searchParams.get("t") || "0";
    const rowMenuActions = useMemo(
        () => [
            copyShareUrlAction({
                buildUrl: (row) => buildShipmentShareUrl(row, { tab: shareTab }),
                disabled: (row) =>
                    !String(
                        row?.customerOrderNumber ||
                            row?.itemId ||
                            row?.trackingNumber ||
                            ""
                    ).trim(),
                onCopied: () =>
                    setSnack({
                        open: true,
                        color: "success",
                        message: "Link copied.",
                    }),
            }),
        ],
        [shareTab]
    );

    const clientOnlyFilterActive = useMemo(
        () =>
            filter === "ready-to-ship" ||
            activeConditions.some((c) => CLIENT_ONLY_SHIPMENT_FILTER_FIELDS.has(c.field)),
        [activeConditions, filter]
    );

    const serverFilterConditions = useMemo(
        () =>
            activeConditions.filter((c) => {
                if (CLIENT_ONLY_SHIPMENT_FILTER_FIELDS.has(c.field)) return false;
                // Tab filter wins over status column filters.
                if (
                    (filter === "needs-label" || filter === "ready-to-ship") &&
                    c.field === "status"
                ) {
                    return false;
                }
                return true;
            }),
        [activeConditions, filter]
    );

    const filtersParam = useMemo(() => {
        if (!serverFilterConditions.length) return "";
        return JSON.stringify({
            conjunction: gridConjunction,
            conditions: serverFilterConditions.map(({ field, operator, value }) => ({
                field,
                operator,
                value,
            })),
        });
    }, [serverFilterConditions, gridConjunction]);

    const layout = useGridLayout(GRID_LAYOUT_KEYS.shipments, {
        sortModel: DEFAULT_SHIPMENT_SORT,
        columnVisibilityModel: {},
    });

    // Local sort state is the source of truth for the grid + API. Persisting
    // through useGridLayout alone was losing non-default column sorts on re-render.
    const [sortModel, setSortModel] = useState(() =>
        layout.sortModel?.length ? layout.sortModel : DEFAULT_SHIPMENT_SORT
    );

    const sortForApi = useMemo(() => {
        const entry = sortModel[0];
        if (entry?.field) {
            return {
                sort: entry.field,
                sortDir: entry.sort === "asc" ? "asc" : "desc",
            };
        }
        return { sort: "dateCreated", sortDir: "desc" };
    }, [sortModel]);

    const onSortModelChange = useCallback(
        (model) => {
            const next = model?.length ? model : DEFAULT_SHIPMENT_SORT;
            setSortModel(next);
            layout.onSortModelChange(next);
            setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
        },
        [layout.onSortModelChange]
    );

    useEffect(() => {
        setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    }, [filtersParam, filter, searchQ]);

    const listUrl = useMemo(() => {
        const params = new URLSearchParams();
        params.set("page", String(clientOnlyFilterActive ? 1 : paginationModel.page + 1));
        params.set(
            "pageSize",
            String(clientOnlyFilterActive ? 2000 : paginationModel.pageSize)
        );
        // Prefer sortBy (same as invoices) — some stacks treat `sort` specially.
        params.set("sortBy", sortForApi.sort);
        params.set("sort", sortForApi.sort);
        params.set("sortDir", sortForApi.sortDir);
        // Ready to Ship is Needs Label ∩ pick-ready (pick readiness is client-side).
        if (filter === "needs-label" || filter === "ready-to-ship") {
            params.set("needsLabel", "1");
        }
        if (searchQ) params.set("q", searchQ);
        if (filtersParam) params.set("filters", filtersParam);
        return `api/broylesllc/shipping/get-shipments?${params.toString()}`;
    }, [
        clientOnlyFilterActive,
        paginationModel.page,
        paginationModel.pageSize,
        filter,
        searchQ,
        filtersParam,
        sortForApi.sort,
        sortForApi.sortDir,
    ]);

    const { data, isLoading, load, refetch } = useApiData(listUrl, {
        // Quiet background refresh while viewing the grid; skip when the
        // shipment drawer is open so PrintOrder form state isn't wiped.
        refetchInterval: printOpen ? false : 15_000,
        refetchIntervalInBackground: false,
    });

    useEffect(() => {
        const items = Array.isArray(data?.items)
            ? data.items
            : Array.isArray(data)
              ? data
              : [];
        setOrders(items);
        setRowCount(typeof data?.total === "number" ? data.total : items.length);
    }, [data]);

    const rows = useMemo(() => {
        let mapped = (orders || []).map(mapShipmentRow);
        if (filter === "ready-to-ship") {
            mapped = mapped.filter((row) => Boolean(row.pickReadyYes));
        }
        if (
            activeConditions.some((c) => CLIENT_ONLY_SHIPMENT_FILTER_FIELDS.has(c.field))
        ) {
            mapped = applyFilter(mapped);
        }
        return mapped;
    }, [orders, applyFilter, activeConditions, filter]);

    const gridSlotProps = useMemo(
        () => rowContextMenuSlotProps(rows, appDataGridSlotProps),
        [rowContextMenuSlotProps, rows]
    );

    const selectedIds = useMemo(
        () => selectionIds(selectionModel, rows),
        [selectionModel, rows]
    );

    const openPrint = useCallback((row) => {
        const groupId = String(row?.combineGroupId || "").trim();
        let docs = [];
        if (groupId) {
            docs = (orders || []).filter(
                (item) => String(item.combineGroupId || "").trim() === groupId
            );
        }
        if (!docs.length) {
            const record = (orders || []).find(
                (item) => String(item._id) === String(row.id)
            );
            if (record) docs = [record];
        }
        if (!docs.length) return;
        setSelectedRecords(docs.map(toPrintRecord));
        setPrintOpen(true);
    }, [orders]);

    const selectedRows = useMemo(
        () =>
            selectedIds
                .map((id) => rows.find((row) => String(row.id) === String(id)))
                .filter(Boolean),
        [selectedIds, rows]
    );

    const combineEligibility = useMemo(
        () => canCombineShipmentRows(selectedRows),
        [selectedRows]
    );
    const canUncombineSelection = useMemo(
        () => canUncombineShipmentRows(selectedRows),
        [selectedRows]
    );

    const openEditSelected = () => {
        if (selectedIds.length === 1) {
            const row = rows.find((item) => String(item.id) === String(selectedIds[0]));
            if (row) openPrint(row);
            return;
        }
        if (selectedIds.length > 1) {
            const groupIds = [
                ...new Set(
                    selectedRows
                        .map((row) => String(row.combineGroupId || "").trim())
                        .filter(Boolean)
                ),
            ];
            if (groupIds.length === 1 && selectedRows.every((row) => row.combineGroupId)) {
                openPrint(selectedRows[0]);
            }
        }
    };

    const openCombineSelected = () => {
        if (!combineEligibility.ok) {
            setSnack({
                open: true,
                color: "warning",
                message: combineEligibility.message || "Cannot combine selection.",
            });
            return;
        }
        setCombineRows(selectedRows);
        setCombineOpen(true);
    };

    const closeCombine = async (didCombine) => {
        setCombineOpen(false);
        setCombineRows([]);
        if (didCombine) {
            setSelectionModel(emptySelectionModel());
            await refetch?.();
        }
    };

    const uncombineSelected = async () => {
        if (!canUncombineSelection) return;
        const groupId = String(selectedRows[0]?.combineGroupId || "").trim();
        if (!groupId) return;
        setUncombineLoad(true);
        const { success, error } = await PostData(
            { combineGroupId: groupId },
            "broylesllc/shipments/uncombine",
            null
        );
        setUncombineLoad(false);
        if (success) {
            setSelectionModel(emptySelectionModel());
            setSnack({
                open: true,
                color: "success",
                message: "Shipments uncombined.",
            });
            await refetch?.();
            return;
        }
        const message =
            error?.response?.data?.message ||
            error?.message ||
            "Could not uncombine shipments.";
        setSnack({ open: true, color: "error", message: String(message) });
    };

    const openCancel = useCallback((row) => {
        if (!row?.customerOrderNumber || row.status === "Shipped") return;
        setCancelOrder({
            id: row.orderMongoId || undefined,
            orderChannel: row.orderChannel,
            customerOrderNumber: row.customerOrderNumber,
            quantity: row.quantity,
        });
        setCancelOpen(true);
    }, []);

    const closeCancel = async (didCancel) => {
        setCancelOpen(false);
        setCancelOrder(null);
        if (didCancel) {
            await refetch?.();
        }
    };

    const openVoidLabel = useCallback((row) => {
        if (!canVoidBreadBsktLabel(row)) return;
        setVoidShipment({
            id: row.id,
            customerOrderNumber: row.customerOrderNumber,
            trackingNumber: row.trackingNumber,
        });
        setVoidOpen(true);
    }, []);

    const closeVoidLabel = async (didVoid) => {
        setVoidOpen(false);
        setVoidShipment(null);
        if (didVoid) {
            await refetch?.();
        }
    };

    const closePrint = (result) => {
        setPrintOpen(false);
        setSelectedRecords([]);
        setSelectionModel(emptySelectionModel());
        if (result === "success" || result === "split") refetch?.();
    };

    useEffect(() => {
        setSelectionModel(emptySelectionModel());
    }, [filter, searchQ, filtersParam, paginationModel.page]);

    const pullOrders = async (action) => {
        if (rangeError) return;
        setPulling(true);
        setPullError("");
        setPullMessage("");
        try {
            const response = await PostData(
                { action, startDate, endDate, syncStatuses: true },
                "broylesllc/shipping/get-orders",
                null
            );
            if (!response?.success) {
                const message = response?.error?.response?.data?.message;
                setPullError(message || "Could not pull orders for that date range.");
                return;
            }
            const summary = response?.data || {};
            const parts = [];
            if (summary.ebay != null) {
                parts.push(
                    summary.ebay === "failed" ? "eBay failed" : `${summary.ebay} eBay`
                );
            }
            if (summary.walmart != null) {
                parts.push(
                    summary.walmart === "failed"
                        ? "Walmart failed"
                        : `${summary.walmart} Walmart`
                );
            }
            if (summary.shopify != null) {
                parts.push(
                    summary.shopify === "failed"
                        ? "Shopify failed"
                        : `${summary.shopify} Shopify`
                );
            }
            const warningText = Array.isArray(summary.warnings) && summary.warnings.length
                ? ` ${summary.warnings.join("; ")}.`
                : "";
            setPullMessage(
                parts.length
                    ? `Pulled ${parts.join(", ")} for ${startDate}–${endDate} (ET).${warningText}`
                    : `Pull finished for ${startDate}–${endDate} (ET).${warningText}`
            );
            await refetch?.();
        } finally {
            setPulling(false);
        }
    };

    const baseColumns = useMemo(
        () => [
            { field: "trackingNumber", headerName: "Tracking", minWidth: 170 },
            {
                field: "combineGroupId",
                headerName: "Combined",
                minWidth: 110,
                sortable: false,
                renderCell: (params) => {
                    const groupId = String(params.value || "").trim();
                    if (!groupId) {
                        return (
                            <Typography component="span" sx={{ color: "#9aa3ad", fontSize: 13 }}>
                                —
                            </Typography>
                        );
                    }
                    return (
                        <Chip
                            size="small"
                            label="Combined"
                            sx={{
                                height: 24,
                                fontWeight: 700,
                                fontSize: 12,
                                borderRadius: "8px",
                                backgroundColor: "#e0f2fe",
                                color: "#0369a1",
                                border: "1px solid #7dd3fc",
                            }}
                        />
                    );
                },
            },
            {
                field: "trackingUrl",
                headerName: "URL",
                minWidth: 170,
                sortable: false,
                renderCell: (params) => {
                    const raw = String(params.value || "").trim();
                    if (!raw) {
                        return (
                            <Typography component="span" sx={{ color: "#9aa3ad", fontSize: 13 }}>
                                —
                            </Typography>
                        );
                    }
                    const href = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
                    return (
                        <Link
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.5,
                                fontWeight: 600,
                                color: "#0d9488",
                                fontSize: 13,
                            }}
                        >
                            Open in New Tab
                            <OpenInNewIcon sx={{ fontSize: 14 }} />
                        </Link>
                    );
                },
            },
            {
                field: "customerOrderNumber",
                headerName: "Order",
                minWidth: 150,
                renderCell: (params) => {
                    const label = params.value || "";
                    if (!label) {
                        return (
                            <Typography component="span" sx={{ color: "#9aa3ad", fontSize: 13 }}>
                                —
                            </Typography>
                        );
                    }
                    const query = new URLSearchParams({
                        order: label,
                        fromShipment: String(params.row.id),
                    });
                    return (
                        <Link
                            component={RouterLink}
                            to={`/orders?${query.toString()}`}
                            underline="hover"
                            onClick={(e) => e.stopPropagation()}
                            sx={{ fontWeight: 600, color: "#0d9488", fontSize: 13 }}
                        >
                            {label}
                        </Link>
                    );
                },
            },
            { field: "itemId", headerName: "Item ID", minWidth: 160 },
            {
                field: "pickReady",
                headerName: "Ready to ship",
                minWidth: 140,
                sortable: false,
                renderCell: (params) => {
                    const ready = Boolean(params.row.pickReadyYes);
                    const picked = params.row.pickedCount ?? 0;
                    const needed = params.row.neededCount ?? 0;
                    return (
                        <Chip
                            size="small"
                            label={ready ? "Ready" : `${picked}/${needed} picked`}
                            sx={pickReadyChipSx(ready)}
                        />
                    );
                },
            },
            {
                field: "catalogItems",
                headerName: "Catalog items",
                minWidth: 180,
                sortable: false,
                renderCell: (params) => {
                    const items = params.value || [];
                    if (!items.length) {
                        return (
                            <Typography component="span" sx={{ color: "#9aa3ad", fontSize: 13 }}>
                                —
                            </Typography>
                        );
                    }
                    return (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: 0.25,
                                py: 0.25,
                                width: "100%",
                                minWidth: 0,
                            }}
                        >
                            {items.map((item) => {
                                const paramsUrl = new URLSearchParams({
                                    t: String(catalogTabForStatus(item.status)),
                                    q: item.itemId || "",
                                    fromShipment: String(params.row.id),
                                });
                                return (
                                    <Link
                                        key={item._id}
                                        component={RouterLink}
                                        to={`/catalog-items?${paramsUrl.toString()}`}
                                        underline="hover"
                                        onClick={(e) => e.stopPropagation()}
                                        sx={{
                                            fontWeight: 600,
                                            color: "#0d9488",
                                            fontSize: 13,
                                            lineHeight: 1.35,
                                            whiteSpace: "nowrap",
                                            maxWidth: "100%",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                        }}
                                    >
                                        {item.itemId}
                                    </Link>
                                );
                            })}
                        </Box>
                    );
                },
            },
            {
                field: "orderChannel",
                headerName: "Channel",
                minWidth: 120,
                renderCell: (params) => {
                    const text = params.value || "";
                    if (!text) {
                        return (
                            <Typography component="span" sx={{ color: "#9aa3ad", fontSize: 13 }}>
                                —
                            </Typography>
                        );
                    }
                    return <Chip size="small" label={text} sx={channelChipSx(text)} />;
                },
            },
            {
                field: "status",
                headerName: "Label",
                minWidth: 140,
                sortable: false,
                renderCell: (params) => {
                    const status = params.value || "";
                    if (status === "Needs Label") {
                        return (
                            <Chip
                                size="small"
                                label="Print label"
                                icon={<LocalPrintshopOutlinedIcon sx={{ fontSize: "16px !important" }} />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    openPrint(params.row);
                                }}
                                sx={statusChipSx(status)}
                            />
                        );
                    }
                    return <Chip size="small" label="Shipped" sx={statusChipSx(status)} />;
                },
            },
            {
                field: "shippedVia",
                headerName: "Shipped via",
                minWidth: 130,
                renderCell: (params) => {
                    const text = params.value || "";
                    if (!text) {
                        return (
                            <Typography component="span" sx={{ color: "#9aa3ad", fontSize: 13 }}>
                                —
                            </Typography>
                        );
                    }
                    const key = text.toLowerCase();
                    const sx = key === "ebay"
                        ? channelChipSx("ebay")
                        : key === "walmart"
                          ? channelChipSx("walmart")
                          : {
                                height: 24,
                                fontWeight: 700,
                                fontSize: 12,
                                borderRadius: "8px",
                                backgroundColor: "#ecfdf5",
                                color: "#047857",
                                border: "1px solid #6ee7b7",
                            };
                    return <Chip size="small" label={text} sx={sx} />;
                },
            },
            {
                field: "buyerName",
                headerName: "Buyer",
                minWidth: 150,
                renderCell: (params) => {
                    const name = params.value || "";
                    const pendingCount = Number(params.row.buyerPendingOrderCount) || 1;
                    const showMulti =
                        params.row.status === "Needs Label" && pendingCount > 1;
                    if (!showMulti) return name || "";
                    return (
                        <Tooltip title={`Buyer has ${pendingCount} orders to ship`}>
                            <span style={{ color: "#b45309", fontWeight: 600 }}>
                                {name || ""}
                            </span>
                        </Tooltip>
                    );
                },
            },
            { field: "shippingService", headerName: "Service", minWidth: 140 },
            {
                field: "labelCost",
                headerName: "Label cost",
                width: 110,
                align: "right",
                headerAlign: "right",
                valueFormatter: (value) =>
                    value == null || value === "" ? "—" : currencyFormatter(Number(value) || 0, 2),
            },
            { field: "quantity", headerName: "Qty", width: 70, align: "right", headerAlign: "right" },
            { field: "weight", headerName: "Wt", width: 70 },
            { field: "length", headerName: "L", width: 60 },
            { field: "width", headerName: "W", width: 60 },
            { field: "height", headerName: "H", width: 60 },
            { field: "shipCity", headerName: "City", minWidth: 120 },
            { field: "shipState", headerName: "State", width: 80 },
            { field: "shipZip", headerName: "Zip", width: 90 },
            { field: "dateCreated", headerName: "Created", minWidth: 110 },
            {
                field: "labelAsset",
                headerName: "",
                width: 64,
                sortable: false,
                hideable: false,
                renderCell: (params) =>
                    params.value ? (
                        <IconButton
                            size="small"
                            aria-label="Open shipping label"
                            onClick={(e) => {
                                e.stopPropagation();
                                setLabelPreview({
                                    open: true,
                                    asset: params.value,
                                    title: `Label ${params.row.trackingNumber || params.row.customerOrderNumber || ""}`.trim(),
                                });
                            }}
                        >
                            <PictureAsPdfOutlinedIcon sx={{ fontSize: 22, color: "#b91c1c" }} />
                        </IconButton>
                    ) : null,
            },
            {
                field: "voidLabel",
                headerName: "Void",
                minWidth: 130,
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                renderCell: (params) => {
                    if (!canVoidBreadBsktLabel(params.row)) {
                        return (
                            <Typography component="span" sx={{ color: "#9aa3ad", fontSize: 12 }}>
                                —
                            </Typography>
                        );
                    }
                    return (
                        <Button
                            size="small"
                            variant="contained"
                            disableElevation
                            startIcon={<UndoOutlinedIcon fontSize="small" />}
                            onClick={(event) => {
                                event.stopPropagation();
                                openVoidLabel(params.row);
                            }}
                            sx={{
                                backgroundColor: "#ffedd5",
                                color: "#9a3412",
                                height: 28,
                                fontSize: 12,
                                textTransform: "none",
                                borderRadius: "8px",
                                "&:hover": { backgroundColor: "#fed7aa" },
                            }}
                        >
                            Void label
                        </Button>
                    );
                },
            },
            {
                field: "cancel",
                headerName: "Cancel",
                minWidth: 120,
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                renderCell: (params) => {
                    const canCancel =
                        params.row.status !== "Shipped" &&
                        Boolean(params.row.customerOrderNumber) &&
                        Boolean(params.row.orderChannel);
                    if (!canCancel) {
                        return (
                            <Typography component="span" sx={{ color: "#9aa3ad", fontSize: 12 }}>
                                —
                            </Typography>
                        );
                    }
                    return (
                        <Button
                            size="small"
                            variant="contained"
                            disableElevation
                            startIcon={<CancelOutlinedIcon fontSize="small" />}
                            onClick={(event) => {
                                event.stopPropagation();
                                openCancel(params.row);
                            }}
                            sx={{
                                backgroundColor: "#fee2e2",
                                color: "#991b1b",
                                height: 28,
                                fontSize: 12,
                                textTransform: "none",
                                borderRadius: "8px",
                                "&:hover": { backgroundColor: "#fecaca" },
                            }}
                        >
                            Cancel
                        </Button>
                    );
                },
            },
        ].map((col) =>
            NON_SORTABLE_SHIPMENT_FIELDS.has(col.field)
                ? col
                : { ...col, sortable: true, sortingOrder: ["asc", "desc"] }
        ),
        [openPrint, openCancel, openVoidLabel]
    );

    // applyWidths uses draftWidths so re-renders after resize keep the new size
    // instead of snapping back to the last committed layout.
    const columns = useMemo(
        () => layout.applyWidths(baseColumns),
        [baseColumns, layout.applyWidths, layout.draftWidths]
    );

    return (
        <div className="catalog-grid-shell">
            <div className="catalog-toolbar">
                <TextField
                    size="small"
                    placeholder="Search tracking, order, buyer…"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    sx={catalogSearchFieldSx}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchOutlinedIcon sx={{ fontSize: 18, color: "#9aa3ad" }} />
                            </InputAdornment>
                        ),
                    }}
                />
                <div className="catalog-toolbar-spacer" />
                <GridLayoutControls layout={layout} columns={baseColumns} />
                {selectedIds.length > 0 ? (
                    <span className="catalog-selection-hint">
                        {selectedIds.length} selected
                    </span>
                ) : pullError ? (
                    <Typography component="span" sx={{ color: "#b91c1c", fontSize: 13 }}>
                        {pullError}
                    </Typography>
                ) : pullMessage ? (
                    <Typography component="span" sx={{ color: "#0f766e", fontSize: 13 }}>
                        {pullMessage}
                    </Typography>
                ) : (
                    <span className="catalog-selection-hint">
                        {(clientOnlyFilterActive ? rows.length : rowCount).toLocaleString()}{" "}
                        shipment
                        {(clientOnlyFilterActive ? rows.length : rowCount) === 1 ? "" : "s"}
                        {filter === "needs-label"
                            ? " needing labels"
                            : filter === "ready-to-ship"
                              ? " ready to ship"
                              : ""}
                    </span>
                )}
                <Button
                    size="small"
                    variant="outlined"
                    disabled={
                        selectedIds.length !== 1 &&
                        !(
                            selectedIds.length > 1 &&
                            selectedRows.length > 1 &&
                            [
                                ...new Set(
                                    selectedRows
                                        .map((row) => String(row.combineGroupId || "").trim())
                                        .filter(Boolean)
                                ),
                            ].length === 1
                        )
                    }
                    startIcon={<EditOutlinedIcon fontSize="small" />}
                    onClick={openEditSelected}
                    sx={catalogQuietButtonSx}
                >
                    Edit
                </Button>
                <Button
                    size="small"
                    variant="outlined"
                    disabled={!combineEligibility.ok}
                    startIcon={<CallMergeOutlinedIcon fontSize="small" />}
                    onClick={openCombineSelected}
                    sx={catalogQuietButtonSx}
                >
                    Combine
                </Button>
                <Button
                    size="small"
                    variant="outlined"
                    disabled={!canUncombineSelection || uncombineLoad}
                    startIcon={<CallSplitOutlinedIcon fontSize="small" />}
                    onClick={uncombineSelected}
                    sx={catalogQuietButtonSx}
                >
                    Uncombine
                </Button>
                <PullOrdersSplitButton
                    pulling={pulling}
                    onPull={pullOrders}
                    startDate={startDate}
                    endDate={endDate}
                    onStartChange={setStartDate}
                    onEndChange={setEndDate}
                    rangeError={rangeError}
                    hint="Pulls marketplace orders, assigns Listed units, and opens Pending Label lines here for labels."
                />
            </div>
            <GridFilterBar {...filterBar} />
            <div className="catalog-grid-main">
                <DataGrid
                    {...appDataGridDefaultProps}
                    loading={Boolean(load || isLoading || pulling)}
                    rows={rows}
                    columns={columns}
                    checkboxSelection
                    disableRowSelectionOnClick
                    getRowHeight={() => "auto"}
                    getEstimatedRowHeight={() => 48}
                    rowSelectionModel={selectionModel}
                    onRowSelectionModelChange={setSelectionModel}
                    sortingMode={clientOnlyFilterActive ? "client" : "server"}
                    sortModel={sortModel}
                    onSortModelChange={onSortModelChange}
                    columnVisibilityModel={layout.columnVisibilityModel}
                    onColumnVisibilityModelChange={layout.onColumnVisibilityModelChange}
                    onColumnWidthChange={layout.onColumnWidthChange}
                    paginationMode={clientOnlyFilterActive ? "client" : "server"}
                    rowCount={clientOnlyFilterActive ? rows.length : rowCount}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    pageSizeOptions={[25, 50, 100]}
                    slotProps={gridSlotProps}
                    sx={{
                        ...catalogDataGridSx,
                        "& .MuiDataGrid-cell": {
                            ...catalogDataGridSx["& .MuiDataGrid-cell"],
                            alignItems: "flex-start",
                            py: 1,
                        },
                        "& .MuiDataGrid-cellCheckbox": {
                            alignItems: "center",
                        },
                    }}
                    localeText={{
                        noRowsLabel:
                            filter === "needs-label"
                                ? "No shipments need a label."
                                : filter === "ready-to-ship"
                                  ? "No shipments are ready to ship yet."
                                  : "No shipments yet. Pull orders to start.",
                    }}
                />
            </div>

            <GridRowContextMenu
                menu={rowMenu}
                onClose={closeRowMenu}
                actions={rowMenuActions}
            />
            {snack.open && (
                <SnackbarEx
                    color={snack.color}
                    message={snack.message}
                    open={snack.open}
                    close={() => setSnack({ open: false, color: "", message: "" })}
                />
            )}

            <Drawer
                anchor="right"
                open={printOpen}
                onClose={() => closePrint()}
                sx={{
                    "& .MuiDrawer-paper": {
                        height: "100%",
                        width: { xs: "100%", sm: "min(640px, 42vw)" },
                        minWidth: { sm: 420 },
                        borderRadius: "14px 0 0 14px",
                        overflow: "hidden",
                    },
                }}
            >
                <PrintOrder
                    record={selectedRecords}
                    // Update existing Airtable shipping-label row when present; else create.
                    isSplit={
                        selectedRecords?.[0]?.shipmentType === "Split" ||
                        (typeof selectedRecords?.[0]?.id === "string" &&
                            selectedRecords[0].id.startsWith("rec"))
                    }
                    onLinked={() => refetch?.()}
                    onSplitSuccess={() => closePrint("split")}
                    close={(val) => closePrint(val)}
                />
            </Drawer>

            <FloatingAssetPreview
                open={labelPreview.open}
                asset={labelPreview.asset}
                title={labelPreview.title || "Shipping label"}
                onClose={() =>
                    setLabelPreview({ open: false, asset: null, title: "" })
                }
            />

            <CancelOrderDialog
                isOpen={cancelOpen}
                onClose={() => closeCancel(false)}
                onSuccess={() => closeCancel(true)}
                orderChannel={cancelOrder?.orderChannel}
                orderId={cancelOrder}
            />

            <VoidLabelDialog
                isOpen={voidOpen}
                onClose={() => closeVoidLabel(false)}
                onSuccess={() => closeVoidLabel(true)}
                shipment={voidShipment}
            />

            <CombineShipmentsDialog
                isOpen={combineOpen}
                onClose={() => closeCombine(false)}
                onSuccess={() => closeCombine(true)}
                shipments={combineRows}
            />
        </div>
    );
}
